package org.teddapp.project.ui.projects

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Project
import com.tedd.db.ProjectStatus
import com.tedd.db.Task
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.LocalUseFullScreenForm
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.adaptive.WindowClass
import org.teddapp.project.ui.adaptive.WithWindowClass
import org.teddapp.project.ui.nav.PillarTabs
import org.teddapp.project.ui.nav.NavIntent
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.nav.TeddBottomNav
import org.teddapp.project.ui.nav.TeddNavRail
import org.teddapp.project.ui.settings.SettingsScreen
import androidx.compose.foundation.layout.statusBarsPadding

/**
 * Écran Projets unique. La logique vit dans [ProjectsModel] ; la mise en page
 * s'adapte à la largeur : compacte avec sélecteur de projet en feuille, kanban
 * en pages et détail plein écran ; étendue avec barre latérale, kanban en
 * colonnes avec glisser-déposer et détail en panneau latéral.
 */
@Composable
fun ProjectsScreen(
    repos: TeddRepositories,
    onNavigate: (Section) -> Unit = {},
    modifier: Modifier = Modifier,
    /** Vue ouverte à l'arrivée, et tâche dont le détail s'ouvre : pour un lien direct ou une capture. */
    initialView: ProjectView = ProjectView.Kanban,
    initialTaskId: String? = null,
) {
    val model = rememberProjectsModel(repos)
    remember(model) { model.view = initialView; model.openTaskId = initialTaskId }
    // Venu d'un lien : on ouvre le projet demandé plutôt que la liste.
    LaunchedEffect(Unit) {
        org.teddapp.project.ui.nav.NavIntent.consumeProject()?.let { model.selectedProjectId = it }
    }
    WithWindowClass(modifier.fillMaxSize()) { wc ->
        CompositionLocalProvider(LocalUseFullScreenForm provides (wc == WindowClass.Compact)) {
            when (wc) {
                WindowClass.Compact -> ProjectsCompactLayout(model, repos, onNavigate)
                else -> ProjectsExpandedLayout(model, repos, onNavigate)
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Étendu (et moyen)
// ──────────────────────────────────────────────────────────────────────
@Composable
private fun ProjectsExpandedLayout(model: ProjectsModel, repos: TeddRepositories, onNavigate: (Section) -> Unit) {
    var settingsOpen by remember { mutableStateOf(false) }
    val selected = model.selectedProject

    Box(modifier = Modifier.fillMaxSize()) {
        Column(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
            Row(modifier = Modifier.fillMaxSize()) {
                TeddNavRail(active = Section.Projects, onNavigate = onNavigate, onSettingsClick = { settingsOpen = true })
                ProjectsSidebar(
                    organizations = model.organizations,
                    projects = model.projects,
                    selectedProjectId = selected?.id,
                    expandedOrgs = model.organizations.map { it.id }.filter { model.isOrgExpanded(it) }.toSet(),
                    onToggleOrg = { model.toggleOrg(it) },
                    onSelectProject = { model.selectedProjectId = it },
                    onOpenManager = { model.orgsManagerOpen = true },
                    onNewProject = { model.newProject(it) },
                    repos = repos,
                )
                Column(modifier = Modifier.fillMaxSize().weight(1f)) {
                    PillarTabs(current = Section.Projects, onNavigate = onNavigate, compact = false)
                    if (selected == null) {
                        ProjectsEmptyState()
                    } else {
                        ProjectMain(
                            project = selected,
                            org = model.selectedOrg,
                            repos = repos,
                            view = model.view,
                            onViewChange = { model.view = it },
                            onOpenTask = { model.openTaskId = it },
                            onNewStatus = { model.statusFormMode = StatusFormMode.New },
                            onEditStatus = { model.statusFormMode = StatusFormMode.Edit(it) },
                            onDeleteStatus = { model.confirmDeleteStatus = it },
                            onEditProject = { model.projectFormMode = ProjectFormMode.Edit(selected) },
                            onDeleteProject = { model.confirmDeleteProject = selected },
                            dragState = model.dragState,
                            onSendToFocus = { model.sendTaskToPomodoro(it) },
                            skills = model.skills,
                            onOpenSkill = { NavIntent.openSkill(it); onNavigate(Section.Skills) },
                        )
                    }
                }
            }
        }

        if (model.orgsManagerOpen) {
            OrganizationsManagerDialog(
                organizations = model.organizations,
                onClose = { model.orgsManagerOpen = false },
                onEditOrg = { model.orgsManagerOpen = false; model.orgFormMode = OrgFormMode.Edit(it) },
                onDeleteOrg = { model.orgsManagerOpen = false; model.confirmDeleteOrg = it },
                onCreateOrg = { name, color, icon -> model.createOrganization(name, color, icon) },
            )
        }
        ProjectsDialogs(model, repos)

        Scrim(visible = settingsOpen, onDismiss = { settingsOpen = false })
        AnimatedVisibility(
            visible = settingsOpen,
            enter = slideInHorizontally(tween(280)) { it },
            exit = slideOutHorizontally(tween(220)) { it },
            modifier = Modifier.align(Alignment.CenterEnd),
        ) { SettingsScreen(onClose = { settingsOpen = false }, onOpenBackup = { settingsOpen = false; onNavigate(Section.Backup) }) }

        if (model.dragState.draggingTaskId != null && selected != null) {
            DragGhostOverlay(dragState = model.dragState, project = selected, repos = repos)
        }

        Scrim(visible = model.openTaskId != null, onDismiss = { model.openTaskId = null })
        AnimatedVisibility(
            visible = model.openTaskId != null,
            enter = slideInHorizontally(tween(280)) { it },
            exit = slideOutHorizontally(tween(220)) { it },
            modifier = Modifier.align(Alignment.CenterEnd),
        ) {
            OpenTaskHost(model, repos) { task, statuses ->
                TaskDetailPanel(
                    task = task,
                    statuses = statuses,
                    onClose = { model.openTaskId = null },
                    onUpdate = { title, description, statusId, priority, dueDate -> model.updateTask(task, title, description, statusId, priority, dueDate) },
                    onDelete = { model.deleteTask(task) },
                )
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Compact
// ──────────────────────────────────────────────────────────────────────
@OptIn(ExperimentalFoundationApi::class)
@Composable
private fun ProjectsCompactLayout(model: ProjectsModel, repos: TeddRepositories, onNavigate: (Section) -> Unit) {
    var selectorOpen by remember { mutableStateOf(false) }
    var menuOpen by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val selected = model.selectedProject

    Box(modifier = Modifier.fillMaxSize().background(TeddTokens.Bg)) {
        Column(modifier = Modifier.fillMaxSize()) {
            ProjectsCompactHeader(
                project = selected,
                org = model.selectedOrg,
                onSelectorClick = { selectorOpen = true },
                menuOpen = menuOpen,
                onMenuToggle = { menuOpen = !menuOpen },
                onMenuDismiss = { menuOpen = false },
                onNewProject = { model.newProject(); menuOpen = false },
                onEditProject = selected?.let { p -> { model.projectFormMode = ProjectFormMode.Edit(p); menuOpen = false } },
                onDeleteProject = selected?.let { p -> { model.confirmDeleteProject = p; menuOpen = false } },
                onNewOrganization = { model.orgFormMode = OrgFormMode.New; menuOpen = false },
            )
            if (selected == null) {
                PillarTabs(current = Section.Projects, onNavigate = onNavigate, compact = true, modifier = Modifier.padding(bottom = 8.dp))
                ProjectsCompactEmpty(
                    modifier = Modifier.weight(1f),
                    hasOrganizations = model.organizations.isNotEmpty(),
                    onCreate = { if (model.organizations.isEmpty()) model.orgFormMode = OrgFormMode.New else model.newProject() },
                )
            } else {
                val statuses by repos.projectStatus.observeByProject(selected.id).collectAsState(emptyList())
                val tasks by repos.tasks.observeByProject(selected.id).collectAsState(emptyList())
                val health = remember(tasks, statuses) { computeProjectHealth(tasks, statuses) }
                // Les onglets et le résumé s'effacent quand on fait défiler les tâches : elles
                // prennent alors toute la hauteur de l'écran.
                org.teddapp.project.ui.TeddCollapsingLayout(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    header = {
                        Column {
                            PillarTabs(current = Section.Projects, onNavigate = onNavigate, compact = true, modifier = Modifier.padding(bottom = 8.dp))
                            ProjectHealthStrip(
                                health = health,
                                onOpenTask = { model.openTaskId = it },
                                onSendToFocus = { model.sendTaskToPomodoro(it) },
                                horizontalPadding = 16.dp,
                                done = tasks.size - health.open,
                                total = tasks.size,
                            )
                        }
                    },
                ) {
                Column(modifier = Modifier.fillMaxSize()) {
                // Les tâches, et la façon de les regarder.
                Row(
                    modifier = Modifier.fillMaxWidth().padding(start = 20.dp, end = 16.dp, top = 14.dp, bottom = 6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text("Tâches", color = TeddTokens.Ink, style = org.teddapp.project.ui.TeddType.BodyStrong, modifier = Modifier.weight(1f))
                    ProjectViewToggle(value = model.view, onChange = { model.view = it })
                }
                when (model.view) {
                    ProjectView.Kanban -> {
                        if (statuses.isEmpty()) {
                            ProjectsCompactNoColumn(modifier = Modifier.weight(1f), onCreate = { model.statusFormMode = StatusFormMode.New })
                        } else {
                            val pagerState = rememberPagerState(pageCount = { statuses.size })
                            ProjectsCompactStatusPills(
                                statuses = statuses,
                                currentPage = pagerState.currentPage,
                                tasksByStatus = { sid -> tasks.count { it.status_id == sid } },
                                onSelect = { idx -> scope.launch { pagerState.animateScrollToPage(idx) } },
                                onLongPress = { model.statusFormMode = StatusFormMode.Edit(it) },
                                onAdd = { model.statusFormMode = StatusFormMode.New },
                            )
                            HorizontalPager(state = pagerState, modifier = Modifier.weight(1f).fillMaxWidth(), pageSpacing = 8.dp) { page ->
                                val status = statuses.getOrNull(page) ?: return@HorizontalPager
                                ProjectsCompactSwimlane(
                                    status = status,
                                    tasks = tasks.filter { it.status_id == status.id }.sortedBy { it.order_index },
                                    onOpenTask = { model.openTaskId = it },
                                    onToggleTaskDone = { model.toggleDone(it) },
                                    onSendToPomodoro = { model.sendTaskToPomodoro(it) },
                                )
                            }
                        }
                    }
                    // La même liste que sur ordinateur : ajout au clavier, cases à cocher, filtres.
                    ProjectView.List -> ProjectListView(
                        statuses = statuses,
                        tasks = tasks,
                        repos = repos,
                        onOpenTask = { model.openTaskId = it },
                        projectId = selected.id,
                        modifier = Modifier.weight(1f).fillMaxWidth(),
                        compact = true,
                    )
                }
                }
                }
            }
            TeddBottomNav(active = Section.Projects, onNavigate = onNavigate, modifier = Modifier.navigationBarsPadding())
        }

        if (selected != null) {
            val statuses by repos.projectStatus.observeByProject(selected.id).collectAsState(emptyList())
            // En vue Liste, le champ d'ajout est déjà sous le pouce : pas de bouton flottant en plus.
            if (statuses.isNotEmpty() && model.view == ProjectView.Kanban) {
                FabAddTask(
                    modifier = Modifier.align(Alignment.BottomEnd).padding(end = 20.dp, bottom = 88.dp),
                    onClick = { model.newTaskForStatusId = statuses.first().id },
                )
            }
            model.newTaskForStatusId?.let { statusId ->
                QuickAddTaskSheet(
                    projectId = selected.id,
                    statusId = statusId,
                    onCreate = { title -> model.createTask(selected.id, statusId, title) },
                    onClose = { model.newTaskForStatusId = null },
                )
            }
        }

        Scrim(visible = selectorOpen, onDismiss = { selectorOpen = false }, alpha = 0.42f)
        AnimatedVisibility(
            visible = selectorOpen,
            enter = slideInVertically(tween(280)) { it },
            exit = slideOutVertically(tween(220)) { it },
            modifier = Modifier.align(Alignment.BottomCenter),
        ) {
            ProjectSelectorSheet(
                organizations = model.organizations,
                projects = model.projects,
                selectedProjectId = selected?.id,
                onNewProjectInOrg = { model.newProject(it); selectorOpen = false },
                onNewOrg = { model.orgFormMode = OrgFormMode.New; selectorOpen = false },
                onEditOrg = { model.orgFormMode = OrgFormMode.Edit(it); selectorOpen = false },
                repos = repos,
                onSelect = { model.selectedProjectId = it.id; selectorOpen = false },
                onClose = { selectorOpen = false },
            )
        }

        AnimatedVisibility(
            visible = model.openTaskId != null,
            enter = slideInVertically(tween(280)) { it },
            exit = slideOutVertically(tween(220)) { it },
        ) {
            OpenTaskHost(model, repos) { task, statuses ->
                // Le même détail que sur ordinateur, en plein écran.
                TaskDetailPanel(
                    task = task,
                    statuses = statuses,
                    onClose = { model.openTaskId = null },
                    onUpdate = { title, description, statusId, priority, dueDate -> model.updateTask(task, title, description, statusId, priority, dueDate) },
                    onDelete = { model.deleteTask(task) },
                    modifier = Modifier.fillMaxSize().background(TeddTokens.Bg).statusBarsPadding().navigationBarsPadding(),
                    compact = true,
                )
            }
        }

        ProjectsDialogs(model, repos)
    }
}

/** Texte des objectifs du projet et progression par colonnes terminales. */
@Composable
private fun ProjectsCompactProgress(project: Project, statuses: List<ProjectStatus>, tasks: List<Task>) {
    val terminalIds = remember(statuses) { statuses.filter { it.is_terminal == 1L }.map { it.id }.toSet() }
    val doneCount = tasks.count { it.status_id != null && it.status_id in terminalIds }
    val progress = if (tasks.isNotEmpty()) doneCount.toFloat() / tasks.size else 0f
    val projColor = runCatching { Color(parseHexProjPhone(project.color)) }.getOrElse { TeddTokens.LimeStrong }
    if (project.objectives.isNullOrBlank() && tasks.isEmpty()) return
    Column(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        project.objectives?.takeIf { it.isNotBlank() }?.let { text ->
            Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                TeddIcon(name = "target", size = 13.dp, color = projColor, strokeWidth = 2.dp)
                Text(text = text, color = TeddTokens.InkMuted, fontSize = 12.5.sp, lineHeight = 18.sp)
            }
        }
        if (tasks.isNotEmpty()) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text("Progression", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
                Text("$doneCount/${tasks.size} · ${(progress * 100).toInt()}%", color = TeddTokens.InkMuted, fontSize = 11.5.sp, fontWeight = FontWeight.SemiBold)
            }
            Box(modifier = Modifier.fillMaxWidth().height(8.dp).clip(RoundedCornerShape(TeddTokens.RFull)).background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RFull))) {
                Box(modifier = Modifier.fillMaxWidth(progress).fillMaxHeight().clip(RoundedCornerShape(TeddTokens.RFull)).background(projColor, RoundedCornerShape(TeddTokens.RFull)))
            }
        }
    }
}

@Composable
private fun ProjectsCompactEmpty(modifier: Modifier, hasOrganizations: Boolean, onCreate: () -> Unit) {
    EmptyBlock(
        modifier = modifier,
        title = "Aucun projet",
        body = if (hasOrganizations) "Crée ton premier projet." else "Crée d'abord une organisation, puis un projet.",
        button = if (hasOrganizations) "Nouveau projet" else "Nouvelle organisation",
        onClick = onCreate,
    )
}

@Composable
private fun ProjectsCompactNoColumn(modifier: Modifier, onCreate: () -> Unit) {
    EmptyBlock(modifier = modifier, title = "Aucune colonne", body = "Crée une première colonne.", button = "Nouvelle colonne", onClick = onCreate)
}

@Composable
private fun EmptyBlock(modifier: Modifier, title: String, body: String, button: String, onClick: () -> Unit) {
    Column(
        modifier = modifier.fillMaxWidth().padding(28.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Box(
            modifier = Modifier.size(56.dp).clip(RoundedCornerShape(TeddTokens.RMd)).background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RMd)),
            contentAlignment = Alignment.Center,
        ) { TeddIcon(name = "grid", size = 28.dp, color = TeddTokens.LimeStrong) }
        Spacer(modifier = Modifier.height(14.dp))
        Text(title, color = TeddTokens.Ink, fontSize = 16.sp, fontWeight = FontWeight.SemiBold)
        Spacer(modifier = Modifier.height(4.dp))
        Text(body, color = TeddTokens.InkMuted, fontSize = 12.sp)
        Spacer(modifier = Modifier.height(16.dp))
        TeddButton(
            text = button, onClick = onClick, kind = TeddButtonKind.Primary, size = TeddButtonSize.Md,
            leadingIcon = { TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.OnAction, strokeWidth = 2.2.dp) },
        )
    }
}

// ──────────────────────────────────────────────────────────────────────
// Partagé : tâche ouverte, dialogues, voile
// ──────────────────────────────────────────────────────────────────────

/** Résout la tâche ouverte et les colonnes du projet, puis délègue au panneau. */
@Composable
private fun OpenTaskHost(
    model: ProjectsModel,
    repos: TeddRepositories,
    content: @Composable (Task, List<ProjectStatus>) -> Unit,
) {
    val project = model.selectedProject
    val statuses by (project?.let { repos.projectStatus.observeByProject(it.id) } ?: flowOf(emptyList())).collectAsState(emptyList())
    val tasks by (project?.let { repos.tasks.observeByProject(it.id) } ?: flowOf(emptyList())).collectAsState(emptyList())
    val task = tasks.firstOrNull { it.id == model.openTaskId }
    if (task != null) content(task, statuses)
}

@Composable
private fun ProjectsDialogs(model: ProjectsModel, repos: TeddRepositories) {
    model.orgFormMode?.let { mode ->
        val initial = (mode as? OrgFormMode.Edit)?.org
        OrganizationFormDialog(
            initial = initial,
            existingNames = model.organizations.filter { it.id != initial?.id }.map { it.name },
            onClose = { model.orgFormMode = null },
            onSave = { name, color, icon -> model.saveOrganization(initial, name, color, icon) },
        )
    }
    model.projectFormMode?.let { mode ->
        val (initial, orgId) = when (mode) {
            is ProjectFormMode.New -> null to mode.organizationId
            is ProjectFormMode.Edit -> mode.project to mode.project.organization_id
        }
        val org = model.organizations.firstOrNull { it.id == orgId }
        ProjectFormDialog(
            initial = initial,
            organizationName = org?.name ?: "—",
            existingNames = model.projects.filter { it.organization_id == orgId && it.id != initial?.id }.map { it.name },
            skills = model.skills,
            onClose = { model.projectFormMode = null },
            onSave = { name, color, icon, description, objectives, skillId ->
                model.saveProject(initial, orgId, name, color, icon, description, objectives, skillId)
            },
        )
    }
    model.statusFormMode?.let { mode ->
        val project = model.selectedProject
        if (project != null) {
            val statuses by repos.projectStatus.observeByProject(project.id).collectAsState(emptyList())
            val initial = (mode as? StatusFormMode.Edit)?.status
            ProjectStatusFormDialog(
                initial = initial,
                existingNames = statuses.filter { it.id != initial?.id }.map { it.name },
                onClose = { model.statusFormMode = null },
                onSave = { name, color, isTerminal -> model.saveStatus(project.id, initial, statuses.size, name, color, isTerminal) },
            )
        }
    }
    model.confirmDeleteOrg?.let { org ->
        val count = model.projects.count { it.organization_id == org.id }
        ProjectsConfirmDelete(
            title = "Supprimer l'organisation ?",
            itemName = org.name,
            description = if (count > 0) "$count projet(s) seront aussi supprimés." else "Cette action est définitive.",
            onCancel = { model.confirmDeleteOrg = null },
            onConfirm = { model.deleteOrganization(org) },
        )
    }
    model.confirmDeleteProject?.let { proj ->
        ProjectsConfirmDelete(
            title = "Supprimer le projet ?",
            itemName = proj.name,
            description = "Les tâches du projet seront aussi supprimées.",
            onCancel = { model.confirmDeleteProject = null },
            onConfirm = { model.deleteProject(proj) },
        )
    }
    model.confirmDeleteStatus?.let { status ->
        ProjectsConfirmDelete(
            title = "Supprimer la colonne ?",
            itemName = status.name,
            description = "Les tâches de cette colonne deviendront sans statut.",
            onCancel = { model.confirmDeleteStatus = null },
            onConfirm = { model.deleteStatus(status) },
        )
    }
}

@Composable
private fun Scrim(visible: Boolean, onDismiss: () -> Unit, alpha: Float = 0.32f) {
    AnimatedVisibility(visible = visible, enter = fadeIn(tween(180)), exit = fadeOut(tween(180))) {
        Box(
            modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = alpha))
                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onDismiss),
        )
    }
}
